/**
 * LocationLandingPage Duck
 * 
 * Redux state management for the LocationLandingPage.
 * Handles fetching location landing page data from the API.
 */

import { storableError } from '../../util/errors';

// ================ Action types ================ //

export const FETCH_LOCATION_DATA_REQUEST = 'app/LocationLandingPage/FETCH_LOCATION_DATA_REQUEST';
export const FETCH_LOCATION_DATA_SUCCESS = 'app/LocationLandingPage/FETCH_LOCATION_DATA_SUCCESS';
export const FETCH_LOCATION_DATA_ERROR = 'app/LocationLandingPage/FETCH_LOCATION_DATA_ERROR';

// ================ Reducer ================ //

const initialState = {
    data: null,
    fetchInProgress: false,
    fetchError: null,
};

export default function reducer(state = initialState, action = {}) {
    const { type, payload } = action;

    switch (type) {
        case FETCH_LOCATION_DATA_REQUEST:
            return {
                ...state,
                data: null, // Clear old data to prevent showing stale content
                fetchInProgress: true,
                fetchError: null,
            };

        case FETCH_LOCATION_DATA_SUCCESS:
            return {
                ...state,
                data: payload,
                fetchInProgress: false,
            };

        case FETCH_LOCATION_DATA_ERROR:
            return {
                ...state,
                fetchInProgress: false,
                fetchError: payload,
            };

        default:
            return state;
    }
}

// ================ Action creators ================ //

export const fetchLocationDataRequest = () => ({
    type: FETCH_LOCATION_DATA_REQUEST,
});

export const fetchLocationDataSuccess = data => ({
    type: FETCH_LOCATION_DATA_SUCCESS,
    payload: data,
});

export const fetchLocationDataError = error => ({
    type: FETCH_LOCATION_DATA_ERROR,
    payload: error,
    error: true,
});

// ================ Thunks ================ //

export const fetchLocationData = (locationSlug, regenerate = false) => async dispatch => {
    dispatch(fetchLocationDataRequest());

    try {
        const queryString = regenerate ? '?regenerate' : '';
        const response = await fetch(`/api/location-landing/${encodeURIComponent(locationSlug)}${queryString}`);

        if (!response.ok) {
            throw new Error(`Failed to fetch location data: ${response.status}`);
        }

        const data = await response.json();
        dispatch(fetchLocationDataSuccess(data));
        return data;
    } catch (error) {
        console.error('Error fetching location data:', error);
        dispatch(fetchLocationDataError(storableError(error)));
        throw error;
    }
};

// ================ Load data ================ //

export const loadData = (params, search, config) => dispatch => {
    const { location } = params;
    const searchParams = new URLSearchParams(search);
    const regenerate = searchParams.has('regenerate');
    return dispatch(fetchLocationData(location, regenerate));
};
